import React, { useState } from "react";
import { ReactSVG } from "react-svg";
import { formatDateToDD_MMM_YYYY } from "../../../../helpers/functions";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedMedicationId } from "../../../../redux/modules/medications/actions";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { reasonOptions } from "../service-steps/clinical-assessment/data";
import NewToolTip from "../../../common/New-ToolTip";

const PrescribedRxTable = ({ 
  headerName, 
  records
}) => {
  const dispatch = useDispatch();
  const { selectedMedicationId } = useSelector((state) => state.medications);
  const [openDetailsId, setOpenDetailsId] = useState(null);

  // Records are already filtered by the parent component
  const filteredRecords = records || [];

  const getReasonLabel = (reasonValue) => {
    if (!reasonValue) return null;
    const reasonOption = reasonOptions.find((reason) => reason.value === reasonValue);
    return reasonOption?.label || reasonValue;
  };

  const formatSafeDate = (value) => {
    if (!value) return "N/A";
    const date = new Date(value);
    if (Number.isNaN(date.getTime()) || date.getUTCFullYear() <= 2000) {
      return "N/A";
    }
    return formatDateToDD_MMM_YYYY(date);
  };

  return (
    <div className="flex flex-col">
      <div className="rounded-[4px] border border-gray-100 overflow-x-auto">
        <div className="h-full overflow-y-auto">
          <table className="min-w-full">
            <thead className="sticky top-0 z-10 bg-white">
              <tr className="h-[52px] bg-gray-50 font-medium text-base text-left text-gray-800 px-[10px] py-[10px]">
                <th scope="col" className="w-[20%] pl-6">Minor Ailment Name</th>
                <th scope="col" className="w-[11%] pl-2 border-l-[1px] border-gray-100">Date of Service</th>
                <th scope="col" className="pl-2 border-l-[1px] border-gray-100 whitespace-nowrap">Prescribed Prescriptions</th>
                <th scope="col" className="w-[35%] pl-2 border-l-[1px] border-gray-100">Prescription Details</th>
                <th scope="col" className="w-[15%] pl-2 border-gray-100">Comments</th>
              </tr>
            </thead>
            <tbody className="min-w-full bg-white text-sm text-gray-800 gap-4">
              {filteredRecords?.map((med, i) => {
                const isHighlighted = selectedMedicationId === med.id;
                const isEvenRow = i % 2 === 0;
                return (
                  <React.Fragment key={med.id}>
                  <tr
                    className={`min-h-[44px] transition-colors duration-300 cursor-pointer group hover:bg-blue-100 ${
                      isHighlighted ? "bg-teal-25" : isEvenRow ? "bg-white" : "bg-gray-10"
                    }`}
                    onClick={() => {
                      dispatch(setSelectedMedicationId(med.id));
                    }}
                  >
                    {/* Minor Ailment Name */}
                    <td className="w-[26%] pl-6 py-[10px] text-gray-800 text-sm font-normal">
                      {med.minorAilmentName || "N/A"}
                    </td>

                    {/* Date of Service */}
                    <td className="w-[9%] py-[10px] pr-[10px] border-l-[1px] border-gray-100 pl-[10px] text-gray-800 text-sm font-normal">
                      {formatSafeDate(
                        med.created || med.createdAt || med.startDate
                      )}
                    </td>

                    {/* Prescribed Prescriptions */}
                    <td className="py-[10px] pr-[10px] pl-[10px] border-l-[1px] border-gray-100 text-gray-800 text-sm font-normal align-top">
                      <div className="flex flex-col">
                        <span className="capitalize">
                          {med.manufacturerCode ? `${med.manufacturerCode} - ${med.medicationName}` : med.medicationName || "N/A"}
                        </span>
                        {med.brand && (
                          <span className="text-gray-600">
                            ({med.brand})
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Prescription Details */}
                    <td className="w-[35%] py-[10px] border-l-[1px] border-gray-100 pr-[10px] pl-[10px]">
                      <div className="flex items-start">
                        <div className="flex-1">
                          {(() => {
                            const parts = [];
                            if (med.dosage && med.form) parts.push(`${med.dosage} ${med.form}`);
                            else if (med.form) parts.push(med.form);
                            if (med.route) parts.push(med.route);
                            if (med.frequency) parts.push(med.frequency);
                            if (med.prn) {
                              let prnText = "PRN";
                              if (med.prnReason) prnText += ` / ${med.prnReason}`;
                              if (med.reason) {
                                const reasonLabel = getReasonLabel(med.reason);
                                prnText += ` (${reasonLabel})`;
                              }
                              parts.push(prnText);
                            } else if (med.reason) {
                              const reasonLabel = getReasonLabel(med.reason);
                              parts.push(reasonLabel);
                            }

                            const details = parts.length > 0 ? parts.join(", ") : "N/A";
                            const duration = med.duration ? med.unit ? `${med.duration} ${med.unit}` : `${med.duration}` : "N/A";
                            const startDate = med.duration ? formatSafeDate(med.startDate) : "N/A";
                            const endDate = med.duration ? formatSafeDate(med.endDate) : "N/A";
                            const specialInstructions = (med.comments || med.specialInstructions || "").toString().trim();
                            const rawQuantity = med.dispenseQuantity || med.quantity || null;
                            const quantity = rawQuantity ? (typeof rawQuantity === "object" ? (rawQuantity.value || rawQuantity.label) : `${rawQuantity}`) : "N/A";
                            const refills = med.refills ? `${med.refills}` : "N/A";
                            const extraMedicationDetails = [];
                            if (specialInstructions) {
                              extraMedicationDetails.push(`Special Instructions: ${specialInstructions}`);
                            }

                            return (
                              <span>
                                {`${details}`}
                                <br />
                                {`Duration: ${duration}, Start Date: ${startDate}, End Date: ${endDate}`}
                                <br />
                                {`QTY ${quantity}, Refills ${refills}${extraMedicationDetails.length > 0 ? `, ${extraMedicationDetails.join(", ")}` : ""}`}
                              </span>
                            );
                          })()}
                            {openDetailsId === med.id && (
                              <>
                                <div className="-ml-[10px] mt-[8px]">
                                  <div className="flex w-full items-center py-[5px] bg-yellow-60 border-b border-t border-b-yellow-75 border-t-yellow-75 px-3">
                                    <div className="whitespace-nowrap">
                                      <span>{`Rx Status: ${med.rxStatus || med.status || "Active"}`}</span>
                                    </div>
                                    <div className="flex items-center gap-2 whitespace-nowrap ml-auto">
                                      <span>{`Prescriber Name: ${med.prescriberName || med.prescriber || med.pharmacistName || med.pharmacist || med.maServicePharmacistName || "N/A"}`}</span>
                                      <ReactSVG
                                        src="/icons/information-dark.svg"
                                        className="w-4 h-4"
                                        data-tooltip-id={`pending-rx-tooltip-${med.id}`}
                                      />
                                      <NewToolTip
                                        id={`pending-rx-tooltip-${med.id}`}
                                        render={() => (
                                          <div className="max-w-[360px] rounded-[8px] bg-gray-10 p-3 text-sm text-teal-300">
                                            <p className="font-semibold mb-1">Prescription Details</p>
                                            <p>Rx Number, DIN #, First Filled Date, Last Filled Date will auto-populate once there is integration with the Pharmacy Management System.</p>
                                          </div>
                                        )}
                                        clickable={true}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </>
                            )}
                        </div>
                      </div>
                    </td>
                    <td className="w-[15%] py-[10px] text-sm text-gray-800 font-normal pr-[12px] pl-[10px] align-top">
                      <div className="flex items-start justify-between">
                        {(() => {
                          const commentsNotes = (med.commentNotes || med.commentsNotes || "").toString().trim();
                          return <span>{commentsNotes}</span>;
                        })()}
                        <button
                          className={`p-[6px] mt-[2px] ${
                            openDetailsId === med.id
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDetailsId((prev) => (prev === med.id ? null : med.id));
                          }}
                        >
                          {openDetailsId === med.id ? (
                            <ChevronUpIcon className="w-6 h-6 stroke-1 stroke-gray-800" />
                          ) : (
                            <ChevronDownIcon className="w-6 h-6 stroke-1 stroke-gray-800" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {(!filteredRecords || filteredRecords.length === 0) && (
          <div className="p-8 text-center text-gray-500">
            <p>No prescribed medications found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrescribedRxTable;
